import React, { useEffect, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';
import { useTenant } from '../../context/TenantContext';
import { useAuth } from '../../context/AuthContext';
import { exportCsv, exportExcel, exportPdf } from '../../utils/export';


const invoiceStatusLabel = (status) => {
  const map = {
    draft: 'borrador',
    sent: 'enviado',
    paid: 'pagado',
    cancelled: 'cancelado'
  };
  return map[status] || status || '';
};

const emptyLine = () => ({
  description: '',
  quantity: 1,
  unit_price: 0,
  tax_rate: 21,
  discount_percent: 0,
});

const Invoices = () => {
  const { activeTenant, onboardingStatus } = useTenant();
  const { isOperator } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [createData, setCreateData] = useState({
    client_name: '',
    client_nif: '',
    client_email: '',
    client_address: '',
    client_country: 'ES',
    invoice_date: '',
    due_date: '',
    concept: '',
    payment_method: 'transferencia',
    notes: '',
    apply_retention: false,
    retention_percent: 15,
    lines: [emptyLine()],
  });
  const [editData, setEditData] = useState({
    client_name: '',
    client_nif: '',
    client_email: '',
    client_address: '',
    client_country: 'ES',
    due_date: '',
    concept: '',
    payment_method: 'transferencia',
    notes: '',
    status: 'draft',
    lines: [emptyLine()],
  });

  const loadInvoices = async () => {
    if (!activeTenant) return;
    try {
      const res = await api.get(`/native/invoices/${activeTenant}`);
      setInvoices(res.data);
    } catch (error) {
      console.error('Error loading invoices:', error);
      toast.error('Error al cargar facturas');
    }
  };

  useEffect(() => {
    loadInvoices();
  }, [activeTenant]);

  const isReadyToOperate = onboardingStatus?.is_ready_to_operate === true;
  const hasSubmittedOnboarding =
    onboardingStatus?.onboarding_status && onboardingStatus.onboarding_status !== 'DRAFT';
  const canOperate = isOperator() || isReadyToOperate;

  const addLine = (setter, data) => {
    setter({ ...data, lines: [...data.lines, emptyLine()] });
  };

  const removeLine = (setter, data, index) => {
    const lines = data.lines.filter((_, i) => i !== index);
    setter({ ...data, lines: lines.length ? lines : [emptyLine()] });
  };

  const updateLine = (setter, data, index, field, value) => {
    const lines = data.lines.map((line, i) =>
      i === index ? { ...line, [field]: value } : line
    );
    setter({ ...data, lines });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!canOperate) {
      toast.error('Cuenta en revision');
      return;
    }
    if (!activeTenant) return;
    try {
      await api.post('/native/invoices', {
        tenant_id: activeTenant,
        client_name: createData.client_name,
        client_nif: createData.client_nif,
        client_email: createData.client_email || null,
        client_address: createData.client_address || null,
        client_country: createData.client_country,
        invoice_date: createData.invoice_date,
        due_date: createData.due_date || null,
        concept: createData.concept,
        lines: createData.lines.map((line) => ({
          description: line.description,
          quantity: Number(line.quantity),
          unit_price: Number(line.unit_price),
          tax_rate: Number(line.tax_rate),
          discount_percent: Number(line.discount_percent || 0),
        })),
        payment_method: createData.payment_method,
        notes: createData.notes || null,
        apply_retention: createData.apply_retention,
        retention_percent: Number(createData.retention_percent),
      });
      toast.success('Factura creada');
      setCreateOpen(false);
      setCreateData({
        client_name: '',
        client_nif: '',
        client_email: '',
        client_address: '',
        client_country: 'ES',
        invoice_date: '',
        due_date: '',
        concept: '',
        payment_method: 'transferencia',
        notes: '',
        apply_retention: false,
        retention_percent: 15,
        lines: [emptyLine()],
      });
      loadInvoices();
    } catch (error) {
      console.error('Error creating invoice:', error);
      toast.error('Error al crear la factura');
    }
  };

  const openEdit = (invoice) => {
    setSelectedInvoice(invoice);
    setEditData({
      client_name: invoice.client_name || '',
      client_nif: invoice.client_nif || '',
      client_email: invoice.client_email || '',
      client_address: invoice.client_address || '',
      client_country: invoice.client_country || 'ES',
      due_date: invoice.due_date ? invoice.due_date.slice(0, 10) : '',
      concept: invoice.concept || '',
      payment_method: invoice.payment_method || 'transferencia',
      notes: invoice.notes || '',
      status: invoice.status || 'draft',
      lines: invoice.lines?.length
        ? invoice.lines.map((line) => ({
            description: line.description || '',
            quantity: line.quantity || 1,
            unit_price: line.unit_price || 0,
            tax_rate: line.tax_rate || 21,
            discount_percent: line.discount_percent || 0,
          }))
        : [emptyLine()],
    });
    setEditOpen(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!selectedInvoice || !activeTenant) return;
    if (!canOperate) {
      toast.error('Cuenta en revision');
      return;
    }
    try {
      await api.put(`/native/invoices/${activeTenant}/${selectedInvoice.id}`, {
        client_name: editData.client_name,
        client_nif: editData.client_nif,
        client_email: editData.client_email || null,
        client_address: editData.client_address || null,
        due_date: editData.due_date || null,
        concept: editData.concept,
        lines: editData.lines.map((line) => ({
          description: line.description,
          quantity: Number(line.quantity),
          unit_price: Number(line.unit_price),
          tax_rate: Number(line.tax_rate),
          discount_percent: Number(line.discount_percent || 0),
        })),
        payment_method: editData.payment_method,
        notes: editData.notes || null,
        status: editData.status,
      });
      toast.success('Factura actualizada');
      setEditOpen(false);
      setSelectedInvoice(null);
      loadInvoices();
    } catch (error) {
      console.error('Error updating invoice:', error);
      toast.error('Error al actualizar la factura');
    }
  };

  const handleDelete = async (invoice) => {
    if (!activeTenant) return;
    if (!canOperate) {
      toast.error('Cuenta en revision');
      return;
    }
    const confirmed = window.confirm(`Eliminar factura ${invoice.invoice_number}?`);
    if (!confirmed) return;
    try {
      await api.delete(`/native/invoices/${activeTenant}/${invoice.id}`);
      toast.success('Factura eliminada');
      loadInvoices();
    } catch (error) {
      console.error('Error deleting invoice:', error);
      toast.error('Error al eliminar la factura');
    }
  };

  const invoiceExportRows = invoices.map((inv) => ({
    invoice_number: inv.invoice_number,
    date: inv.invoice_date,
    client: inv.client_name,
    total: inv.total,
    status: invoiceStatusLabel(inv.status),
  }));

  return (
    <div data-testid="client-invoices-page" className="max-w-6xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="text-4xl font-heading font-bold">Facturacion</h1>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => exportCsv(invoiceExportRows, 'facturas.csv')}>
            Exportar CSV
          </Button>
          <Button variant="outline" onClick={() => exportExcel(invoiceExportRows, 'facturas.xlsx')}>
            Exportar Excel
          </Button>
          <Button variant="outline" onClick={() => exportPdf(invoiceExportRows, 'facturas.pdf', 'Facturas')}>
            Exportar PDF
          </Button>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="gap-2" data-testid="create-invoice-button" disabled={!canOperate}>
                <Plus className="w-4 h-4" />Nueva Factura
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl">
              <DialogHeader>
                <DialogTitle>Nueva Factura</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Cliente</Label>
                    <Input
                      value={createData.client_name}
                      onChange={(e) => setCreateData({ ...createData, client_name: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label>NIF</Label>
                    <Input
                      value={createData.client_nif}
                      onChange={(e) => setCreateData({ ...createData, client_nif: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={createData.client_email}
                      onChange={(e) => setCreateData({ ...createData, client_email: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Pais</Label>
                    <Input
                      value={createData.client_country}
                      onChange={(e) => setCreateData({ ...createData, client_country: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div>
                  <Label>Direccion</Label>
                  <Textarea
                    value={createData.client_address}
                    onChange={(e) => setCreateData({ ...createData, client_address: e.target.value })}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Fecha factura</Label>
                    <Input
                      type="date"
                      value={createData.invoice_date}
                      onChange={(e) => setCreateData({ ...createData, invoice_date: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label>Fecha vencimiento</Label>
                    <Input
                      type="date"
                      value={createData.due_date}
                      onChange={(e) => setCreateData({ ...createData, due_date: e.target.value })}
                    />
                  </div>
                </div>
                <div>
                  <Label>Concepto</Label>
                  <Textarea
                    value={createData.concept}
                    onChange={(e) => setCreateData({ ...createData, concept: e.target.value })}
                    required
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    id="retention"
                    type="checkbox"
                    checked={createData.apply_retention}
                    onChange={(e) =>
                      setCreateData({ ...createData, apply_retention: e.target.checked })
                    }
                  />
                  <Label htmlFor="retention">Aplicar retencion</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    className="w-24"
                    value={createData.retention_percent}
                    onChange={(e) =>
                      setCreateData({ ...createData, retention_percent: e.target.value })
                    }
                    disabled={!createData.apply_retention}
                  />
                </div>
                <div>
                  <Label>Metodo de pago</Label>
                  <Select
                    value={createData.payment_method}
                    onValueChange={(value) => setCreateData({ ...createData, payment_method: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="transferencia">transferencia</SelectItem>
                      <SelectItem value="tarjeta">tarjeta</SelectItem>
                      <SelectItem value="efectivo">efectivo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Notas</Label>
                  <Textarea
                    value={createData.notes}
                    onChange={(e) => setCreateData({ ...createData, notes: e.target.value })}
                  />
                </div>
                <div className="space-y-3">
                  <Label>Lineas</Label>
                  {createData.lines.map((line, index) => (
                    <div key={index} className="grid grid-cols-6 gap-2 items-end">
                      <div className="col-span-2">
                        <Label>Descripcion</Label>
                        <Input
                          value={line.description}
                          onChange={(e) => updateLine(setCreateData, createData, index, 'description', e.target.value)}
                          required
                        />
                      </div>
                      <div>
                        <Label>Cantidad</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={line.quantity}
                          onChange={(e) => updateLine(setCreateData, createData, index, 'quantity', e.target.value)}
                          required
                        />
                      </div>
                      <div>
                        <Label>Precio</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={line.unit_price}
                          onChange={(e) => updateLine(setCreateData, createData, index, 'unit_price', e.target.value)}
                          required
                        />
                      </div>
                      <div>
                        <Label>IVA %</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.1"
                          value={line.tax_rate}
                          onChange={(e) => updateLine(setCreateData, createData, index, 'tax_rate', e.target.value)}
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button type="button" variant="outline" onClick={() => addLine(setCreateData, createData)}>
                          +
                        </Button>
                        <Button type="button" variant="destructive" onClick={() => removeLine(setCreateData, createData, index)}>
                          -
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
                <Button type="submit" className="w-full">Crear factura</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {!canOperate && !hasSubmittedOnboarding && (
        <div className="mb-6 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-slate-700">
          Completa la incorporacion para activar Facturacion.
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {invoices.map((inv) => (
          <Card key={inv.id}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-sm text-muted-foreground">{inv.invoice_number}</div>
                  <div className="text-lg font-semibold">{inv.client_name}</div>
                  <div className="text-sm text-muted-foreground">{inv.invoice_date}</div>
                </div>
                <div className="text-right">
                  <div className="text-xl font-bold">{inv.total} EUR</div>
                  <div className="text-xs text-muted-foreground">{invoiceStatusLabel(inv.status)}</div>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-4">
                <Button variant="outline" size="sm" onClick={() => openEdit(inv)}>
                  <Pencil className="w-4 h-4 mr-1" />Editar
                </Button>
                <Button variant="destructive" size="sm" onClick={() => handleDelete(inv)}>
                  <Trash2 className="w-4 h-4 mr-1" />Eliminar
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Editar Factura</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Cliente</Label>
                <Input
                  value={editData.client_name}
                  onChange={(e) => setEditData({ ...editData, client_name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>NIF</Label>
                <Input
                  value={editData.client_nif}
                  onChange={(e) => setEditData({ ...editData, client_nif: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={editData.client_email}
                  onChange={(e) => setEditData({ ...editData, client_email: e.target.value })}
                />
              </div>
              <div>
                <Label>Fecha vencimiento</Label>
                <Input
                  type="date"
                  value={editData.due_date}
                  onChange={(e) => setEditData({ ...editData, due_date: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label>Concepto</Label>
              <Textarea
                value={editData.concept}
                onChange={(e) => setEditData({ ...editData, concept: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Metodo de pago</Label>
              <Select
                value={editData.payment_method}
                onValueChange={(value) => setEditData({ ...editData, payment_method: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="transferencia">transferencia</SelectItem>
                  <SelectItem value="tarjeta">tarjeta</SelectItem>
                  <SelectItem value="efectivo">efectivo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notas</Label>
              <Textarea
                value={editData.notes}
                onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
              />
            </div>
            <div>
              <Label>Estado</Label>
              <Select
                value={editData.status}
                onValueChange={(value) => setEditData({ ...editData, status: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">borrador</SelectItem>
                  <SelectItem value="sent">enviado</SelectItem>
                  <SelectItem value="paid">pagado</SelectItem>
                  <SelectItem value="cancelled">cancelado</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-3">
              <Label>Lineas</Label>
              {editData.lines.map((line, index) => (
                <div key={index} className="grid grid-cols-6 gap-2 items-end">
                  <div className="col-span-2">
                    <Label>Descripcion</Label>
                    <Input
                      value={line.description}
                      onChange={(e) => updateLine(setEditData, editData, index, 'description', e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <Label>Cantidad</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={line.quantity}
                      onChange={(e) => updateLine(setEditData, editData, index, 'quantity', e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <Label>Precio</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={line.unit_price}
                      onChange={(e) => updateLine(setEditData, editData, index, 'unit_price', e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <Label>IVA %</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.1"
                      value={line.tax_rate}
                      onChange={(e) => updateLine(setEditData, editData, index, 'tax_rate', e.target.value)}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" onClick={() => addLine(setEditData, editData)}>
                      +
                    </Button>
                    <Button type="button" variant="destructive" onClick={() => removeLine(setEditData, editData, index)}>
                      -
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            <Button type="submit" className="w-full">Guardar cambios</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Invoices;
